import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import useShopper from '~/composables/useShopper' // explicit import to avoid circular dependency when called from productStore setup body

export default function useProducts() {
  const nuxtApp = useNuxtApp()
  const localeCode = nuxtApp.$i18n.locale.value
  const siteId = localeConfig[localeCode]?.siteId
  const locale = localeCode === 'fr' ? localeConfig[localeCode].code : ''
  // SFCC expects the parameter omitted outside French; an empty string is not a valid locale.
  const localeParams: { locale?: string } = locale ? { locale } : {}
  const { shopperProducts, getAuthHeaders } = useShopper()

  function alignVariationValues(product: ShopperProductsTypes.Product): FlattenedProduct {
    const newProduct = { ...product }

    product.variationAttributes?.forEach((attribute) => {
      attribute.values?.forEach((value) => {
        if (product.variationValues?.[attribute.id] === value.value) {
          newProduct[`${attribute.id}Value`] = value.name
        }
      })
    })

    product.imageGroups?.forEach((imageGroup) => {
      imageGroup.variationAttributes?.forEach((attribute) => {
        attribute.values?.forEach((value) => {
          if (product.variationValues?.[attribute.id] === value.value) {
            newProduct.images = imageGroup.images
          }
        })
      })
    })

    // SAFETY: FlattenedProduct only names the SDK's untyped `c_*` and `${attribute}Value` keys; the object is the SDK product.
    return newProduct as FlattenedProduct
  }

  async function getProduct(id: string) {
    if (!id) return null
    try {
      const response = await shopperProducts.getProduct({
        headers: await getAuthHeaders(),
        parameters: {
          id,
          siteId,
          ...localeParams,
          perPricebook: true,
        },
      })
      return alignVariationValues(response)
    } catch (e) {
      console.error(`getProduct ${id}: ${errorLogLine(await salesforceErrorHandler(e))}`)
      return null
    }
  }

  async function getProducts(
    ids: string[],
    options: ShopperProductsTypes.ShopperProductsQueryParameters = { allImages: false },
  ) {
    try {
      const allProducts: FlattenedProduct[] = []
      const batches = []
      const uniqueIds = [...new Set(ids)] // Remove duplicates
      const batchLimit = 24 // The maximum number of productIDs that can be requested are 24.

      for (let i = 0; i < uniqueIds.length; i += batchLimit) {
        const batch = uniqueIds.slice(i, i + batchLimit)
        const parameters = { ids: batch.join(','), siteId, ...localeParams, perPricebook: true, ...options }
        batches.push(
          shareRequest(`products|${JSON.stringify(parameters)}`, async () =>
            shopperProducts.getProducts({ headers: await getAuthHeaders(), parameters }),
          ),
        )
      }

      const responses = await Promise.all(batches)
      responses.forEach((response) => {
        const flattenedProducts = response.data?.map(alignVariationValues)
        if (Array.isArray(flattenedProducts)) {
          allProducts.push(...flattenedProducts)
        }
      })
      return allProducts
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function getCategory(id: string) {
    try {
      return await shopperProducts.getCategory({
        headers: await getAuthHeaders(),
        parameters: {
          id,
          siteId,
          ...localeParams,
        },
      })
    } catch (e) {
      console.error(`getCategory ${id}: ${errorLogLine(await salesforceErrorHandler(e))}`)
      return null
    }
  }

  return {
    getProduct,
    getProducts,
    getCategory,
  }
}
